import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Dices } from "lucide-react";
import { SHILL_LINES, CA, copyText, xShare } from "@/data";

const EASE = [0.22, 1, 0.36, 1];

export default function ShillGenerator() {
  const [index, setIndex] = useState(0);
  const [rollKey, setRollKey] = useState(0);

  const reroll = () => {
    let next = Math.floor(Math.random() * SHILL_LINES.length);
    if (next === index) next = (next + 1) % SHILL_LINES.length;
    setIndex(next);
    setRollKey((k) => k + 1);
  };

  const line = SHILL_LINES[index];

  return (
    <section id="shill-lab" className="relative overflow-hidden py-28 sm:py-36" data-testid="shill-lab-section">
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full bg-[#DC2626] opacity-[0.06] blur-[130px]" />
      <div className="pointer-events-none absolute -right-24 top-10 h-[420px] w-[420px] rounded-full bg-[#E5A93C] opacity-[0.07] blur-[130px]" />

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-center"
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#E5A93C]">
            05 / Shill Lab
          </p>
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl">
            Fire the <span className="text-stroke-gold">hype cannon.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#94A3B8] sm:text-base">
            One click. Instant propaganda. Copy it, post it, spread the movement.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="relative mt-14 rounded-3xl border border-[rgba(229,169,60,0.35)] bg-[#0E1015] px-6 py-14 shadow-[0_0_80px_rgba(229,169,60,0.08)] sm:px-14"
        >
          <span className="absolute left-6 top-4 font-display text-7xl font-black text-[rgba(229,169,60,0.18)]">
            &ldquo;
          </span>
          <div className="flex min-h-[130px] items-center justify-center sm:min-h-[150px]">
            <AnimatePresence mode="wait">
              <motion.p
                key={rollKey}
                initial={{ opacity: 0, y: 24, rotateX: -40 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, y: -24, rotateX: 40 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="text-center font-display text-xl font-bold leading-snug text-[#F8FAFC] sm:text-2xl lg:text-3xl"
                data-testid="shill-output"
              >
                {line}
              </motion.p>
            </AnimatePresence>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-3.5">
            <button
              onClick={reroll}
              data-testid="shill-reroll-button"
              className="group flex items-center gap-2.5 rounded-full bg-[#E5A93C] px-7 py-3.5 font-display text-base font-bold text-[#07080A] transition-all duration-300 hover:bg-[#F6C85F] hover:shadow-[0_0_36px_rgba(246,200,95,0.4)]"
            >
              <Dices className="h-4.5 w-4.5 transition-transform duration-500 group-hover:rotate-180" />
              Fire Again
            </button>
            <button
              onClick={() => copyText(line, "Shill copied — go spread it")}
              data-testid="shill-copy-button"
              className="flex items-center gap-2.5 rounded-full border border-[rgba(229,169,60,0.4)] px-7 py-3.5 font-display text-base font-bold text-[#F8FAFC] transition-all duration-300 hover:border-[#F6C85F] hover:bg-[#07080A]"
            >
              <Copy className="h-4.5 w-4.5 text-[#E5A93C]" />
              Copy Shill
            </button>
            <button
              onClick={() => xShare(`${line}\n\nCA: ${CA}`)}
              data-testid="shill-share-x-button"
              className="flex items-center gap-2.5 rounded-full border border-[rgba(220,38,38,0.5)] px-7 py-3.5 font-display text-base font-bold text-[#F8FAFC] transition-all duration-300 hover:border-[#F87171] hover:bg-[#07080A]"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-[#F87171]" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Post to X
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
